import React, {useEffect, useState} from 'react'
import {DateTime} from '@therms/react-modules'
import {ButtonConfirm, Icon} from '@therms/atalaya'
import {UserSOSComments} from '@src/modules/Users/components/UserSOS/UserSOSComments'
import SOS from '@src/assets/sos.svg'
import {MdCancel, MdCheckCircle, MdCrisisAlert} from 'react-icons/md'

interface ActivateEmergencySOSProps {
  activatedTimestamp?: string
  gpsAccuracy: number
  hasGPSTracking: boolean
  isActivated: boolean
  onActivate: () => void
  onCancel: () => void
  /** If `isActivated=true` this is the User's activated UserSOS ID */
  userSOSId?: string
}

function ActivateEmergencySOS({
  activatedTimestamp,
  gpsAccuracy,
  hasGPSTracking,
  isActivated,
  onActivate,
  onCancel,
  userSOSId,
}: ActivateEmergencySOSProps) {
  const [countdown, setCountdown] = useState<number>(5)

  useEffect(() => {
    if (!isActivated) {
      const t = setTimeout(() => {
        setCountdown((prev) => {
          if (prev === 1) {
            onActivate()
            return 0
          }
          return prev - 1
        })
      }, 1000)

      return () => {
        clearTimeout(t)
      }
    }

    return () => undefined
  }, [countdown, isActivated])

  return (
    <div>
      <div className="bg-critical p-base rounded">
        <div className="gap-y-base flex flex-col">
          <div className="flex justify-center align-middle text-xl">
            <img alt="" src={SOS} style={{height: 50, width: 50}} /> Emergency
            SOS
          </div>

          {hasGPSTracking ? (
            <div className="my-base text-center">
              <Icon Icon={MdCheckCircle} /> GPS Location Tracking (device
              accuracy {gpsAccuracy} meters)
            </div>
          ) : (
            <div className="my-base text-center">
              <Icon Icon={MdCancel} /> GPS is not available for your position
            </div>
          )}

          {!isActivated && countdown > 0 && (
            <div className="w-full text-center">
              <div className="text-color-neutral-light text-lg">
                All On-Duty Users will be notified{' '}
                {hasGPSTracking && ' and sent your GPS location'}
              </div>

              <div className="text-lg">Activating SOS in...</div>

              <div className="my-base flex justify-center">
                <div
                  className="text-color-neutral-dark text-xxl flex flex-col justify-center bg-white"
                  style={{
                    borderRadius: '150px',
                    height: '150px',
                    width: '150px',
                  }}
                >
                  {countdown}
                </div>
              </div>
            </div>
          )}

          {!isActivated && countdown <= 0 && (
            <div className="my-lg w-full text-center">
              <div className="text-color-caution animate-pulse text-xl">
                <Icon color="caution" Icon={MdCrisisAlert} /> Attempting to
                Activate
              </div>
              <em className="text-color-neutral-light text-lg">
                If you do not have internet connection this will fail.
              </em>
            </div>
          )}

          {isActivated && (
            <div className="my-lg w-full text-center">
              <div className="text-color-neutral-light text-lg">
                All On-Duty Users have been notified{' '}
                {hasGPSTracking && ' and sent your GPS location'}
              </div>

              <div className="text-color-white text-xl">Activated</div>

              <div className="text-color-white text-lg">
                <DateTime mode="time" relative timestamp={activatedTimestamp} />
              </div>
            </div>
          )}

          <div className="mt-base w-full">
            <div className="p-sm bg-critical-dark mb-base rounded text-center">
              <div className="font-light">
                This Emergency S.O.S. will <u>not</u> contact emergency
                services.
              </div>

              <div>
                If you need emergency services assistance{' '}
                <u>
                  <strong>call 911</strong>
                </u>
                .
              </div>
            </div>

            <ButtonConfirm
              className="text-xxl h-24"
              confirmationMessage="Are you sure you want to Cancel / Deactivate your Emergency S.O.S.?"
              confirmationVariant="critical"
              fullWidth
              onClick={onCancel}
              size="lg"
              variant="neutral"
            >
              <div className="mr-base">
                <Icon color="critical" Icon={MdCancel} size="xxl" />
              </div>{' '}
              Cancel
            </ButtonConfirm>
          </div>
        </div>
      </div>
      {isActivated && userSOSId && <UserSOSComments userSOSId={userSOSId} />}
    </div>
  )
}

export {ActivateEmergencySOS}
